import { useCallback, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setConnecting,
  setConnected,
  setSessionInfo,
  setPhase,
  addTranscript,
  setUserSpeaking,
  setAISpeaking,
  incrementDuration,
  endCall,
  setError,
  setDemoMode,
  setMicAvailable,
  selectSessionId,
  selectCallPhase,
  selectDemoMode,
  selectMicAvailable,
  selectIsMuted,
} from '../redux/slices/callSlice';
import { voiceAPI } from '../services/api';
import useSpeechRecognition from './useSpeechRecognition';
import useSpeechSynthesis from './useSpeechSynthesis';

const useVoiceChat = () => {
  const dispatch = useDispatch();
  const sessionId = useSelector(selectSessionId);
  const callPhase = useSelector(selectCallPhase);
  const demoMode = useSelector(selectDemoMode);
  const micAvailable = useSelector(selectMicAvailable);
  const isMuted = useSelector(selectIsMuted);

  const timerRef = useRef(null);
  const interactionRef = useRef(false);
  const sessionIdRef = useRef(null);
  const demoModeRef = useRef(false);
  const micAvailableRef = useRef(false);
  const callActiveRef = useRef(false);
  const isMutedRef = useRef(false);

  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);
  useEffect(() => { demoModeRef.current = demoMode; }, [demoMode]);
  useEffect(() => { micAvailableRef.current = micAvailable; }, [micAvailable]);
  useEffect(() => { isMutedRef.current = isMuted; }, [isMuted]);

  const { isListening, transcript, interimTranscript, error: speechError, startListening, stopListening, resetTranscript, setCallActive } = useSpeechRecognition();
  const isListeningRef = useRef(false);
  useEffect(() => { isListeningRef.current = isListening; }, [isListening]);
  const { isSpeaking: isAISpeaking, speak: speakSynthesis, cancel: cancelSpeech } = useSpeechSynthesis();

  useEffect(() => {
    if (transcript && transcript.trim().length > 0 && interactionRef.current && callActiveRef.current && micAvailableRef.current) {
      const msg = transcript.trim();
      resetTranscript();
      handleUserMessage(msg);
    }
  }, [transcript]);

  useEffect(() => {
    if (speechError === 'not-allowed') {
      dispatch(setMicAvailable(false));
      dispatch(setDemoMode(true));
    }
  }, [speechError]);

  const speakText = useCallback((text, callback) => {
    dispatch(setAISpeaking(true));
    speakSynthesis(text, {
      rate: 1.0,
      onend: () => {
        dispatch(setAISpeaking(false));
        if (callback) callback();
      },
    });
  }, [dispatch, speakSynthesis]);

  const initializeCall = useCallback(async (slug, hasMic) => {
    dispatch(setConnecting());
    dispatch(setMicAvailable(hasMic));
    callActiveRef.current = true;
    setCallActive(true);
    try {
      const { data } = await voiceAPI.startConversation(slug || 'creato-software');
      const { sessionId: sid, greeting, conversationId, settings } = data.data;
      sessionIdRef.current = sid;
      dispatch(setSessionInfo({ sessionId: sid, conversationId, greeting, settings }));
      dispatch(setConnected());
      dispatch(setPhase('connected'));
      dispatch(addTranscript({ role: 'assistant', content: greeting }));
      startTimer();
      speakText(greeting, () => {
        if (!callActiveRef.current) return;
        interactionRef.current = true;
        if (hasMic) {
          setTimeout(() => startListening(), 300);
        }
      });
    } catch (error) {
      callActiveRef.current = false;
      setCallActive(false);
      dispatch(setError(error.message || 'Failed to connect. Please try again.'));
    }
  }, [dispatch, speakText, startListening]);

  const handleUserMessage = useCallback(async (message) => {
    const sid = sessionIdRef.current;
    if (!sid || !callActiveRef.current) return;
    dispatch(setUserSpeaking(true));
    dispatch(addTranscript({ role: 'user', content: message }));
    if (micAvailableRef.current) stopListening();
    try {
      const { data } = await voiceAPI.sendMessage(sid, message);
      const reply = data.data.reply;
      dispatch(setUserSpeaking(false));
      dispatch(addTranscript({ role: 'assistant', content: reply }));
      speakText(reply, () => {
        if (!callActiveRef.current) return;
        interactionRef.current = true;
        if (micAvailableRef.current) {
          setTimeout(() => startListening(), 300);
        }
      });
    } catch (error) {
      dispatch(setUserSpeaking(false));
      if (micAvailableRef.current) {
        setTimeout(() => startListening(), 500);
      }
    }
  }, [dispatch, speakText, startListening, stopListening]);

  const endVoiceCall = useCallback(async () => {
    callActiveRef.current = false;
    setCallActive(false);
    interactionRef.current = false;
    if (timerRef.current) clearInterval(timerRef.current);
    stopListening();
    cancelSpeech();
    try {
      const sid = sessionIdRef.current;
      if (sid) {
        await voiceAPI.endConversation(sid);
      }
    } catch (e) {}
    dispatch(endCall());
  }, [dispatch, cancelSpeech, stopListening]);

  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      dispatch(incrementDuration());
    }, 1000);
  };

  const toggleListening = useCallback(() => {
    if (demoModeRef.current) return;
    if (isListeningRef.current) {
      stopListening();
    } else {
      resetTranscript();
      startListening();
    }
  }, [stopListening, resetTranscript, startListening]);

  return {
    initializeCall,
    endVoiceCall,
    toggleListening,
    handleUserMessage,
    isAISpeaking,
    interimTranscript,
    speechError,
  };
};

export default useVoiceChat;
